var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { Circle as GCircle } from '@antv/g';
import { getPortXYByPlacement, getTextStyleByPlacement, isSimplePort } from '../../utils/element';
import { inferIconStyle } from '../../utils/node';
import { getPaletteColors } from '../../utils/palette';
import { getRectIntersectPoint } from '../../utils/point';
import { omitStyleProps, subObject, subStyleProps } from '../../utils/prefix';
import { parseSize } from '../../utils/size';
import { mergeOptions } from '../../utils/style';
import { getWordWrapWidthByBox } from '../../utils/text';
import { BaseElement } from '../base-element';
import { effect } from '../effect';
import { Badge, Icon, Label } from '../shapes';
/**
 * <zh/> 节点元素的基类
 *
 * <en/> Base node class
 * @remarks
 * <zh/> 自定义节点时，建议将此类作为基类。这样，你只需要关注如何实现 keyShape 的绘制逻辑
 *
 * <zh/> 设计文档：https://www.yuque.com/antv/g6/gl1iof1xpzg6ed98
 *
 * <en/> When customizing a node, it is recommended to use this class as the base class. This way, you can directly focus on how to implement the drawing logic of keyShape
 *
 * <en/> Design document: https://www.yuque.com/antv/g6/gl1iof1xpzg6ed98
 */
export class BaseNode extends BaseElement {
    type = 'node';
    static defaultStyleProps = {
        x: 0,
        y: 0,
        size: 32,
        droppable: true,
        draggable: true,
        port: true,
        ports: [],
        portZIndex: 2,
        portLinkToCenter: false,
        badge: true,
        badges: [],
        badgeZIndex: 3,
        halo: false,
        haloDroppable: false,
        haloLineDash: 0,
        haloLineWidth: 12,
        haloStrokeOpacity: 0.25,
        haloPointerEvents: 'none',
        haloZIndex: -1,
        icon: true,
        iconZIndex: 1,
        label: true,
        labelIsBillboard: true,
        labelMaxWidth: '200%',
        labelPlacement: 'bottom',
        labelWordWrap: false,
        labelZIndex: 0,
    };
    constructor(options) {
        super(mergeOptions({ style: BaseNode.defaultStyleProps }, options));
    }
    getSize(attributes = this.attributes) {
        const { size } = attributes;
        return parseSize(size);
    }
    getKeyStyle(attributes) {
        const style = this.getGraphicStyle(attributes);
        return Object.assign(omitStyleProps(style, ['label', 'halo', 'icon', 'badge', 'port']));
    }
    getLabelStyle(attributes) {
        if (attributes.label === false || !attributes.labelText)
            return false;
        const { placement, maxWidth, offsetX, offsetY, ...labelStyle } = subStyleProps(this.getGraphicStyle(attributes), 'label');
        const keyBounds = this.getShape('key').getLocalBounds();
        return Object.assign(getTextStyleByPlacement(keyBounds, placement, offsetX, offsetY), { wordWrapWidth: getWordWrapWidthByBox(keyBounds, maxWidth) }, labelStyle);
    }
    getHaloStyle(attributes) {
        if (attributes.halo === false)
            return false;
        const { fill, ...keyStyle } = this.getKeyStyle(attributes);
        const haloStyle = subStyleProps(this.getGraphicStyle(attributes), 'halo');
        return { ...keyStyle, stroke: fill, ...haloStyle };
    }
    getIconStyle(attributes) {
        if (attributes.icon === false || (!attributes.iconText && !attributes.iconSrc))
            return false;
        const iconStyle = subStyleProps(this.getGraphicStyle(attributes), 'icon');
        return Object.assign(inferIconStyle(attributes.size, iconStyle), iconStyle);
    }
    getBadgesStyle(attributes) {
        const badges = subObject(this.shapeMap, 'badge-');
        const badgesShapeStyle = {};
        Object.keys(badges).forEach((key) => {
            badgesShapeStyle[key] = false;
        });
        if (attributes.badge === false || !attributes.badges?.length)
            return badgesShapeStyle;
        const { badges: badgeOptions = [], badgePalette, opacity = 1, ...restAttributes } = attributes;
        const colors = getPaletteColors(badgePalette);
        const badgeStyle = subStyleProps(this.getGraphicStyle(restAttributes), 'badge');
        badgeOptions.forEach((option, i) => {
            badgesShapeStyle[i] = {
                backgroundFill: colors ? colors[i % colors?.length] : undefined,
                opacity,
                ...badgeStyle,
                ...this.getBadgeStyle(option),
            };
        });
        return badgesShapeStyle;
    }
    getBadgeStyle(style) {
        const keyShape = this.getShape('key');
        const { placement = 'top', offsetX, offsetY, ...restStyle } = style;
        const textStyle = getTextStyleByPlacement(keyShape.getLocalBounds(), placement, offsetX, offsetY, true);
        return { ...textStyle, ...restStyle };
    }
    getPortsStyle(attributes) {
        const ports = this.getPorts();
        const portsShapeStyle = {};
        Object.keys(ports).forEach((key) => {
            portsShapeStyle[key] = false;
        });
        if (attributes.port === false || !attributes.ports?.length)
            return portsShapeStyle;
        const portStyle = subStyleProps(this.getGraphicStyle(attributes), 'port');
        const { ports: portOptions = [] } = attributes;
        portOptions.forEach((option, index) => {
            const key = option.key || index;
            const mergedStyle = { ...portStyle, ...option };
            if (isSimplePort(mergedStyle)) {
                portsShapeStyle[key] = false;
            }
            else {
                const [x, y] = this.getPortXY(attributes, option);
                portsShapeStyle[key] = { transform: `translate(${x}, ${y})`, ...mergedStyle };
            }
        });
        return portsShapeStyle;
    }
    getPortXY(attributes, style) {
        const { placement = 'left' } = style;
        const bounds = this.getShape('key').getLocalBounds();
        return getPortXYByPlacement(bounds, placement);
    }
    /**
     * Get the ports for the node.
     * @returns Ports shape map.
     */
    getPorts() {
        return subObject(this.shapeMap, 'port-');
    }
    /**
     * Get the center point of the node.
     * @returns The center point of the node.
     */
    getCenter() {
        return this.getShape('key').getBounds().center;
    }
    /**
     * Get the point on the outer contour of the node that is the intersection with a line starting in the center the ending in the point `p`.
     * @param point - The point to intersect with the node.
     * @returns The intersection point.
     */
    getIntersectPoint(point) {
        const keyShapeBounds = this.getShape('key').getBounds();
        return getRectIntersectPoint(point, keyShapeBounds);
    }
    drawHaloShape(attributes, container) {
        const keyShape = this.getShape('key');
        this.upsert('halo', keyShape.constructor, this.getHaloStyle(attributes), container);
    }
    drawIconShape(attributes, container) {
        this.upsert('icon', Icon, this.getIconStyle(attributes), container);
    }
    drawBadgeShapes(attributes, container) {
        const badgesStyle = this.getBadgesStyle(attributes);
        Object.keys(badgesStyle).forEach((key) => {
            this.upsert(`badge-${key}`, Badge, badgesStyle[key], container);
        });
    }
    drawPortShapes(attributes, container) {
        const portsStyle = this.getPortsStyle(attributes);
        Object.keys(portsStyle).forEach((key) => {
            this.upsert(`port-${key}`, GCircle, portsStyle[key], container);
        });
    }
    drawLabelShape(attributes, container) {
        this.upsert('label', Label, this.getLabelStyle(attributes), container);
    }
    // 用于装饰抽象方法 / Used to decorate abstract methods
    _drawKeyShape(attributes, container) {
        return this.drawKeyShape(attributes, container);
    }
    render(attributes = this.parsedAttributes, container = this) {
        // 1. key shape
        this._drawKeyShape(attributes, container);
        if (!this.getShape('key'))
            return;
        // 2. halo, use shape same with keyShape
        this.drawHaloShape(attributes, container);
        // 3. icon
        this.drawIconShape(attributes, container);
        // 4. badges
        this.drawBadgeShapes(attributes, container);
        // 5. label
        this.drawLabelShape(attributes, container);
        // 6. ports
        this.drawPortShapes(attributes, container);
    }
    onframe() {
        this.drawBadgeShapes(this.parsedAttributes, this);
        this.drawLabelShape(this.parsedAttributes, this);
    }
}
__decorate([
    effect((self, attributes) => self.getHaloStyle(attributes))
], BaseNode.prototype, "drawHaloShape", null);
__decorate([
    effect((self, attributes) => self.getIconStyle(attributes))
], BaseNode.prototype, "drawIconShape", null);
__decorate([
    effect((self, attributes) => self.getBadgesStyle(attributes))
], BaseNode.prototype, "drawBadgeShapes", null);
__decorate([
    effect((self, attributes) => self.getPortsStyle(attributes))
], BaseNode.prototype, "drawPortShapes", null);
__decorate([
    effect((self, attributes) => self.getLabelStyle(attributes))
], BaseNode.prototype, "drawLabelShape", null);
__decorate([
    effect((self, attributes) => self.getKeyStyle(attributes))
], BaseNode.prototype, "_drawKeyShape", null);
//# sourceMappingURL=base-node.js.map